import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/onboarding`;

function OnboardingForm() {
    const navigate = useNavigate();
  const token = localStorage.getItem("token");

  const [formData, setFormData] = useState({
    full_name: "",
    dob: "",
    gender: "",
    marital_status: "",
    blood_group: "",
    personal_phone_number: "",
    alternate_phone_number: "",
    personal_email_id: "",
    emergency_contact_name: "",
    emergency_contact_number: "",
    current_residential_address: "",
    designation:"",
    department:"",
    joining_date: "",
    account_holder_name: "",
    bank_name: "",
    account_number: "",
    ifsc_code: "",
    pan_number: "",
    highest_qualification: "",
    university_college_name: "",
    year_of_graduation: "",
    last_company: "",
    last_designation: "",
    duration_of_employment: "",
    reason_for_leaving: "",
  });

  // 🧠 Debug tracker
  useEffect(() => {
    console.log("🧠 Form data updated:", formData);
  }, [formData]);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    console.log("✏️ Updating:", name, files ? files[0] : value);
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value, // handle file input
    }));
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("🚀 Submitting formData:", formData);

    if (!formData.full_name) {
      toast.error("Please fill out required fields");
      return;
    }

    try {
      // ✅ Create FormData object
      const formDataToSend = new FormData();

      // --- Separate file fields from normal data ---
      const fileFields = [
        "pan_card",
        "aadhaar_card",
        "experience_letter",
        "bank_proof",
      ];
      const jsonData = {};

      for (const key in formData) {
        if (fileFields.includes(key)) {
          // Only append if file exists
          if (formData[key]) {
            formDataToSend.append(key, formData[key]);
          }
        } else {
          jsonData[key] = formData[key];
        }
      }

      // ✅ Add all text fields as one JSON string
      formDataToSend.append("details", JSON.stringify(jsonData));

      console.log("📦 Sending multipart data:", jsonData);

      // ✅ Send request to backend
      const res = await axios.post(API, formDataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      console.log("✅ Response:", res.data);

      if (res.status === 200 || res.status === 201) {
        toast.success(res.data.message || "Employee details submitted!");
        navigate("/dashboard");
      } else {
        toast.error(res.data.message || "Failed to submit employee details.");
      }
    } catch (err) {
      console.error("❌ API Error:", err.response || err);
      toast.error("Error submitting form. Please try again.");
    }
  };


  return (
    <div className="min-h-screen bg-gray-50 flex justify-center items-center py-10">
      <div className="w-full max-w-5xl bg-white shadow-lg rounded-2xl p-8">
        <h2 className="text-3xl font-semibold text-center text-gray-800 mb-8">
          Employee Onboarding Form
        </h2>

        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* --- Personal Details --- */}
          <div>
            <label className="block text-gray-700">Full Name *</label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Date of Birth *</label>
            <input
              type="date"
              name="dob"
              value={formData.dob}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Gender *</label>
            <select
              name="gender"
              value={formData.gender}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            >
              <option value="">Select</option>
              <option>Male</option>
              <option>Female</option>
              <option>Other</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700">Marital Status</label>
            <select
              name="marital_status"
              value={formData.marital_status}
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            >
              <option value="">Select</option>
              <option>Single</option>
              <option>Married</option>
              <option>Divorced</option>
              <option>Widowed</option>
            </select>
          </div>

          <div>
            <label className="block text-gray-700">Blood Group</label>
            <input
              type="text"
              name="blood_group"
              value={formData.blood_group}
              onChange={handleChange}
              placeholder="e.g. O+"
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Personal Phone Number *
            </label>
            <input
              type="tel"
              name="personal_phone_number"
              value={formData.personal_phone_number}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Alternate Phone Number
            </label>
            <input
              type="tel"
              name="alternate_phone_number"
              value={formData.alternate_phone_number}
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Personal Email *</label>
            <input
              type="email"
              name="personal_email_id"
              value={formData.personal_email_id}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          {/* --- Emergency Details --- */}
          <div>
            <label className="block text-gray-700">
              Emergency Contact Name *
            </label>
            <input
              type="text"
              name="emergency_contact_name"
              value={formData.emergency_contact_name}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Emergency Contact Number *
            </label>
            <input
              type="tel"
              name="emergency_contact_number"
              value={formData.emergency_contact_number}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-700">
              Current Residential Address *
            </label>
            <textarea
              name="current_residential_address"
              value={formData.current_residential_address}
              onChange={handleChange}
              required
              rows="3"
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Designation *</label>
            <input
              type="text"
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Department *</label>
            <input
              type="text"
              name="department"
              value={formData.department}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Joining Date *</label>
            <input
              type="date"
              name="joining_date"
              value={formData.joining_date}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          {/* --- Banking Details --- */}
          <div>
            <label className="block text-gray-700">Account Holder Name *</label>
            <input
              type="text"
              name="account_holder_name"
              value={formData.account_holder_name}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Bank Name *</label>
            <input
              type="text"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Account Number *</label>
            <input
              type="text"
              name="account_number"
              value={formData.account_number}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">IFSC Code *</label>
            <input
              type="text"
              name="ifsc_code"
              value={formData.ifsc_code}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200 uppercase"
            />
          </div>

          <div>
            <label className="block text-gray-700">PAN Number *</label>
            <input
              type="text"
              name="pan_number"
              value={formData.pan_number}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200 uppercase"
            />
          </div>

          {/* --- Education --- */}
          <div>
            <label className="block text-gray-700">
              Highest Qualification *
            </label>
            <input
              type="text"
              name="highest_qualification"
              value={formData.highest_qualification}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              University/College Name *
            </label>
            <input
              type="text"
              name="university_college_name"
              value={formData.university_college_name}
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Year of Graduation *</label>
            <input
              type="number"
              name="year_of_graduation"
              value={formData.year_of_graduation}
              onChange={handleChange}
              required
              min="1950"
              max={new Date().getFullYear()}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          {/* --- Previous Employment --- */}
          <div>
            <label className="block text-gray-700">Last Company</label>
            <input
              type="text"
              name="last_company"
              value={formData.last_company}
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">Last Designation</label>
            <input
              type="text"
              name="last_designation"
              value={formData.last_designation}
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Duration of Employment
            </label>
            <input
              type="text"
              name="duration_of_employment"
              value={formData.duration_of_employment}
              onChange={handleChange}
              placeholder="e.g. 2 years"
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-gray-700">Reason for Leaving</label>
            <textarea
              name="reason_for_leaving"
              value={formData.reason_for_leaving}
              onChange={handleChange}
              rows="3"
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div className="md:col-span-2 mt-4">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">
              Document Uploads
            </h3>
          </div>

          <div>
            <label className="block text-gray-700">
              PAN Card (PDF or Image) *
            </label>
            <input
              type="file"
              name="pan_card"
              accept=".pdf, image/*"
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Aadhaar Card (PDF or Image) *
            </label>
            <input
              type="file"
              name="aadhaar_card"
              accept=".pdf, image/*"
              onChange={handleChange}
              required
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Relieving / Experience Letter
            </label>
            <input
              type="file"
              name="experience_letter"
              accept=".pdf, image/*"
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div>
            <label className="block text-gray-700">
              Cancelled Cheque / Bank Statement (Salary Account Verification)
            </label>
            <input
              type="file"
              name="bank_proof"
              accept=".pdf, image/*"
              onChange={handleChange}
              className="mt-1 w-full p-2 border rounded-lg focus:ring focus:ring-blue-200"
            />
          </div>

          <div className="md:col-span-2 flex justify-center mt-6">
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition"
            >
              Submit Onboarding Form
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default OnboardingForm;
